import { X } from "lucide-react"
import { Button } from "@/components/ui/Button"
import { Input } from "@/components/ui/Input"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/Table"
import { type PlanProduct, SUPPLIERS, productById, units } from "../data"

interface Props {
  planProducts: PlanProduct[]
  onChange: (productId: string, changes: Partial<PlanProduct>) => void
  onRemove: (productId: string) => void
}

const toNumber = (text: string) => (text === "" ? 0 : Math.max(0, Math.round(Number(text))))

// The products in the plan. Supplier and minimum order start from what is on
// record for the product; the buyer can change both here.
export default function PlanProducts({ planProducts, onChange, onRemove }: Props) {
  if (planProducts.length === 0) {
    return <div className="rounded-md bg-muted/50 px-4 py-6 text-center text-sm">No products yet. Add some from the list below.</div>
  }

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Product</TableHead>
          <TableHead className="w-56">Supplier</TableHead>
          <TableHead className="w-32 text-right">Minimum order</TableHead>
          <TableHead className="w-32 text-right">We suggest</TableHead>
          <TableHead className="w-36 text-right">Quantity</TableHead>
          <TableHead className="w-12" />
        </TableRow>
      </TableHeader>
      <TableBody>
        {planProducts.map((planProduct) => {
          const product = productById(planProduct.productId)
          const belowMinimum = planProduct.moq !== null && planProduct.quantity < planProduct.moq
          return (
            <TableRow key={planProduct.productId}>
              <TableCell>
                <div className="font-medium">{product.name}</div>
                <div className="text-xs">{product.sku}</div>
              </TableCell>
              <TableCell>
                <Input
                  list="wireframe-suppliers"
                  value={planProduct.supplier}
                  placeholder="Supplier name"
                  onChange={(e) => onChange(planProduct.productId, { supplier: e.target.value })}
                />
              </TableCell>
              <TableCell>
                <Input
                  type="number"
                  className="text-right tabular-nums"
                  value={planProduct.moq ?? ""}
                  onChange={(e) => onChange(planProduct.productId, { moq: e.target.value === "" ? null : toNumber(e.target.value) })}
                />
              </TableCell>
              <TableCell className="text-right tabular-nums">
                {product.suggested !== null ? units(product.suggested) : product.status === "enough" ? "Enough stock" : "Buyer decides"}
              </TableCell>
              <TableCell>
                <Input
                  type="number"
                  className="text-right tabular-nums"
                  value={planProduct.quantity || ""}
                  onChange={(e) => onChange(planProduct.productId, { quantity: toNumber(e.target.value) })}
                />
                {belowMinimum && <div className="mt-1 text-right text-xs text-warning">Below the minimum order</div>}
              </TableCell>
              <TableCell>
                <Button variant="ghost" size="icon" aria-label={`Remove ${product.name}`} onClick={() => onRemove(planProduct.productId)}>
                  <X className="h-4 w-4" />
                </Button>
              </TableCell>
            </TableRow>
          )
        })}
      </TableBody>
      <datalist id="wireframe-suppliers">
        {SUPPLIERS.map((name) => (
          <option key={name} value={name} />
        ))}
      </datalist>
    </Table>
  )
}
